import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { dateTimeOptions } from '../../utils/dates';
import { getHydrant, getHydrantFlush, getHydrantFlushLatest } from '../../services/api';
import { makeHydrant, makeHydrantFlush } from '../../models';
import CircleButton from '../../components/CircleButton';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import HydrantFlushModal from '../../components/hydrantFlushes/HydrantFlushModal';

const HydrantFlush = () => {
  const [hydrant, setHydrant] = useState(null);
  const [hydrantFlush, setHydrantFlush] = useState(null);
  const [loadingHydrant, setLoadingHydrant] = useState(false);
  const [loadingHydrantFlush, setLoadingHydrantFlush] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const { hydrantId, hydrantFlushId } = useParams();
  const history = useHistory();

  useEffect(() => {
    setupHydrant();
    setupHydrantFlush();
  }, [hydrantId, hydrantFlushId, history.location]);

  const setupHydrant = async () => {
    try {
      setLoadingHydrant(true);
      const result = await getHydrant(hydrantId);
      setHydrant(makeHydrant(result));
      setLoadingHydrant(false);
    } catch (ex) {
      setHydrant(null);
      setLoadingHydrant(false);
    }
  };

  const setupHydrantFlush = async () => {
    try {
      setLoadingHydrantFlush(true);
      const result = hydrantFlushId ? await getHydrantFlush(hydrantFlushId) : await getHydrantFlushLatest(hydrantId);
      setHydrantFlush(makeHydrantFlush(result));
      setLoadingHydrantFlush(false);
    } catch (ex) {
      setHydrantFlush(null);
      setLoadingHydrantFlush(false);
    }
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  const selectFlush = (flushId) => {
    history.push(`/hydrants/${hydrantId}/hydrantFlush/${flushId}`);
  };

  const formatDateTime = (value) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('en-US', dateTimeOptions);
  };

  const durationString = useMemo(() => {
    if (!hydrantFlush?.startDate || !hydrantFlush?.endDate) return '--';
    const start = new Date(hydrantFlush.startDate);
    const end = new Date(hydrantFlush.endDate);
    const durationMs = end.getTime() - start.getTime();

    if (!Number.isFinite(durationMs) || durationMs < 0) return '--';

    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}m ${seconds}s`;
  }, [hydrantFlush]);

  return (
    <div className="hydrant-flush">
      <div className="report-title">
        <div className="title">
          <h3>Hydrant Flush Results</h3>
          <div className="form-links no-print">
            <div className="link" onClick={toggleModal}>Change Flush</div>
          </div>
        </div>
        {loadingHydrant || loadingHydrantFlush || <CircleButton icon={faPrint} size="large" title="Print" onClick={print} className="no-print" />}
      </div>

      {loadingHydrantFlush && <div className="spinner"></div>}

      {!loadingHydrantFlush && hydrantFlush && (
        <div className="hydrant-flush-result-card">
          <div className="hydrant-flush-gallons">
            <div className="hydrant-flush-stat">
              <label>Total Gallons:</label>
              <span>{hydrantFlush?.gallonsUsed ?? 0}</span>
            </div>
          </div>

          <div className="hydrant-flush-detail-rows">
            <div className="hydrant-flush-detail-row-one">
              <div className="hydrant-flush-detail-row">
                <label>Hydrant:</label>
                <span>{hydrant?.name || hydrantFlush?.hydrant?.name || '--'}</span>
              </div>
              <div className="hydrant-flush-detail-row hydrant-flush-detail-row-wide">
                <label>Flow Device:</label>
                <span>{hydrantFlush?.flowDevice?.name || '--'}</span>
              </div>
            </div>
            <div className="hydrant-flush-detail-row-two">
              <div className="hydrant-flush-detail-row">
                <label>Start Clarity:</label>
                <span>{hydrantFlush?.startClarity}</span>
              </div>
              <div className="hydrant-flush-detail-row">
                <label>During Clarity:</label>
                <span>{hydrantFlush?.duringClarity}</span>
              </div>
              <div className="hydrant-flush-detail-row">
                <label>End Clarity:</label>
                <span>{hydrantFlush?.endClarity}</span>
              </div>
            </div>
            <div className="hydrant-flush-detail-row-two">
              <div className="hydrant-flush-detail-row">
                <label>Flow PSI:</label>
                <span>{hydrantFlush?.maxPsi ?? 0}</span>
              </div>
              <div className="hydrant-flush-detail-row">
                <label>Flow GPM:</label>
                <span>{hydrantFlush?.maxGpm ?? 0}</span>
              </div>
            </div>
            <div className="hydrant-flush-detail-row-two">
              <div className="hydrant-flush-detail-row">
                <label>Start:</label>
                <span>{formatDateTime(hydrantFlush?.startDate)}</span>
              </div>
              <div className="hydrant-flush-detail-row">
                <label>End:</label>
                <span>{formatDateTime(hydrantFlush?.endDate)}</span>
              </div>
              <div className="hydrant-flush-detail-row">
                <label>Duration:</label>
                <span>{durationString}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loadingHydrantFlush && !hydrantFlush && (
        <div className="empty-body">
          <div className="empty-text">No Flush Found</div>
          <div className="empty-text-small">We couldn't find a flush result.</div>
        </div>
      )}

      <HydrantFlushModal
        hydrantId={hydrantId}
        open={modalOpen}
        toggle={toggleModal}
        onSelect={selectFlush}
        hydrantFlush={hydrantFlush}
      />
    </div>
  );
};

export default HydrantFlush;